import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { IonContent, IonHeader, IonToolbar, IonItem, IonCard, IonCardContent, useIonAlert, IonIcon, IonGrid, IonRow, IonCol, IonInput, IonThumbnail, IonNote, IonButton, IonSpinner, IonButtons, IonPage, IonNavLink, IonSelect, IonSelectOption, SelectCustomEvent, IonTitle } from '@ionic/react';
import './TradePage.css';
import { arrowBackOutline, closeOutline, syncOutline } from "ionicons/icons";
import { getAssetExchangeQuote } from '../../services/stellar/horizon/dex/dex';
import NumberFormat from "react-number-format";
import { Asset } from "../../types/types";
import { useAccountDetailsQuery } from "../../services/api/stellar";
import { useAppSelector } from '../../services/redux/hook';
import { decimalAdjust } from "../../services/utils/decimalAdjust";
import TradeConfirmationPage from "./TradeConfirmation";
import { Horizon } from "@stellar/stellar-sdk";

import useCryptoMode from "../../hooks/useCryptoMode";
import { Provider } from "react-redux";
import { dispatch, store } from "@/services/redux/store";
import { closeModal } from "@/services/redux/slices";


interface Props extends PropsWithChildren {
    assetFrom?: Asset;
    assetTo?: Asset;
}

const Page: React.FC<Props> = ({ assetFrom, assetTo }) => {

    const nav = document.querySelector("ion-nav");
    const { cryptoMode } = useCryptoMode();

    let balances: any;

    const { t, i18n } = useTranslation();
    const walletInfo = useAppSelector((state) => state.walletInfo);


    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);
    if (accountDetails && accountDetails.details.id) {

        balances = accountDetails.details.balances;
    }

    const close = () => {
        dispatch(closeModal());
    }

    // Get tradeable assets based on crypto mode
    const tradeableAssets = React.useMemo(() => {
        return supportedAssets.filter((a: Asset) => {
            if (cryptoMode) {
                return (a.type === 'crypto' || a.type === 'fiat') && a.trade_enabled === true;
            } else {
                return a.type === 'fiat' && a.trade_enabled === true;
            }
        });
    }, [supportedAssets, cryptoMode]);

    // Set default assets if not provided
    const defaultFromAsset = assetFrom || tradeableAssets[0];
    const defaultToAsset = assetTo || tradeableAssets[1] || tradeableAssets[0];

    const fromAssetAmountInitial = 0;
    const toAssetAmountInitial = 0;

    const [fromAssetAmount, setFromAssetAmount] = useState<number>(fromAssetAmountInitial);
    const [tradingPath, setTradingPath] = useState<any>([])
    const [toAssetAmount, setToAssetAmount] = useState(toAssetAmountInitial);
    const [toAsset, setToAsset] = useState<Asset>(defaultToAsset);
    const [toAssetsArray, setToAssetsArray] = useState<Asset[]>([]);

    const [fromAsset, setFromAsset] = useState<Asset>(defaultFromAsset);

    const [isSpinner, setIsSpinner] = useState(false);

    const [marketLabel, setMarketLabel] = useState('');

    const [canTrade, setCanTrade] = useState(false);
    const [loading, setLoading] = useState(true);
    const [disableButton, setDisableButton] = useState(true);
    const [present] = useIonAlert();
    const pageRef = useRef();
    const [goBack, setGoBack] = useState(false);


    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }

    const showTradeConfirmation = () => {

        nav?.push(() =>

            <TradeConfirmationPage
                fAsset={fromAsset}
                tAsset={toAsset}
                fAssetAmount={fromAssetAmount}
                tAssetAmount={toAssetAmount}
                path={tradingPath}
            />

        )
    }

    const getSwapQuote = async (sendAmount: number) => {

        if ((fromAsset && toAsset) && (fromAsset.code != toAsset.code) && (sendAmount != fromAssetAmount)) {

            let source_amount = 0;

            setIsSpinner(true)

            let records: Horizon.ServerApi.PaymentPathRecord[] = []

            if (fromAsset.code === 'XLM') {

                const toAssetIdentifier = toAsset.identifier.split(':');

                let [error, response] = await getAssetExchangeQuote(sendAmount.toString(), undefined, undefined, toAssetIdentifier[0], toAssetIdentifier[1]);

                if (error) {
                    setIsSpinner(false);
                    setDisableButton(true)
                    return
                }

                if (response) {

                    records = response

                }

            }

            if (toAsset.code === 'XLM') {

                const fromAssetIdentifier = fromAsset.identifier.split(':');

                let [error, response] = await getAssetExchangeQuote(sendAmount.toString(), fromAssetIdentifier[0], fromAssetIdentifier[1], undefined, undefined);

                if (error) {
                    setIsSpinner(false);
                    setDisableButton(true)
                    return
                }

                if (response) {

                    records = response

                }

            }

            if (fromAsset.code != 'XLM' && toAsset.code != 'XLM') {

                const fromAssetIdentifier = fromAsset.identifier.split(':');
                const toAssetIdentifier = toAsset.identifier.split(':');

                let [error, response] = await getAssetExchangeQuote(sendAmount.toString(), fromAssetIdentifier[0], fromAssetIdentifier[1], toAssetIdentifier[0], toAssetIdentifier[1]);

                if (error) {
                    //presentErrorAlert(JSON.stringify(error));
                    setIsSpinner(false);
                    setDisableButton(true)
                    return
                }

                if (response) {

                    records = response

                }
            }

            if (records && records.length > 0) {

                const source_asset_code = records[0].source_asset_code || 'XLM';
                source_amount = parseFloat(records[0].source_amount);
                const destination_asset_code = records[0].destination_asset_code || 'XLM';
                const destination_amount = parseFloat(records[0].destination_amount);
                const path = records[0].path;


                const market = `1 ${source_asset_code} ~ ${(destination_amount / source_amount).toFixed(toAsset.decimal_places)} ${destination_asset_code} `

                setToAssetAmount(parseFloat(records[0].destination_amount));
                setMarketLabel(market);
                setTradingPath(path);

            }

            if (records && records.length == 0) {
                const error = t('trade.NO-MARKET')
                presentErrorAlert(error);
                setIsSpinner(false)
                setDisableButton(true);
                return
            }

            enableTradeButton(source_amount)
            setIsSpinner(false)
        }

    }

    const swapFromToAsset = () => {

        if (toAsset && fromAsset) {

            const newFromAsset = toAsset;
            const newToAsset = fromAsset;

            setFromAsset(newFromAsset);
            setToAsset(newToAsset);
            setFromAssetAmount(0)
            setToAssetAmount(0)
            enableTradeButton(0)

        }
    }

    const tradeAmount = (amount: string) => {

        if (fromAsset && toAsset) {

            if (amount.indexOf(',') > -1) {
                amount = amount.replace(',', '.')

            }

            const tradeAmount = +parseFloat(amount).toFixed(fromAsset.decimal_places)

            if (!tradeAmount || isNaN(tradeAmount)) {
                setDisableButton(true);
                setFromAssetAmount(0);
                return
            }

            if (balances && balances[fromAsset.identifier]) {

                const fromAssetPercentage = (tradeAmount / (balances[fromAsset.identifier].available.toNumber()) * 100).toFixed(5);
                const value = parseFloat(fromAssetPercentage).toFixed(4)
                setFromAssetAmount(tradeAmount);
                getSwapQuote(tradeAmount)
            }
        }

    }

    const enableTradeButton = (source_amount: number) => {

        if (balances && fromAsset && balances[fromAsset.identifier]) {

            const availableBalance = decimalAdjust(balances[fromAsset.identifier].available, -fromAsset.decimal_places);

            if (source_amount > 0 && source_amount <= availableBalance) {

                setDisableButton(false)

            }

            else (
                setDisableButton(true)
            )
        }

        setIsSpinner(false)
    }

    useEffect(() => {

        // Check if we can trade:
        // - At least one asset must have balance > 0 (to trade FROM)
        // - The other asset must exist in the account (balance >= 0, even if 0)
        const fromAssetBalance = fromAsset && balances && balances[fromAsset.identifier]
            ? balances[fromAsset.identifier].available.toNumber()
            : undefined;

        const toAssetBalance = toAsset && balances && balances[toAsset.identifier]
            ? balances[toAsset.identifier].available.toNumber()
            : undefined;

        // Both assets must exist in account (not undefined), and at least one must have balance > 0
        const canTradeCondition =
            fromAssetBalance !== undefined // fromAsset exists in account
            && toAssetBalance !== undefined // toAsset exists in account
            && (fromAssetBalance > 0 || toAssetBalance > 0) // At least one has balance > 0
            && toAssetsArray.length > 0; // Ensure there are assets to trade to

        if (canTradeCondition) {
            setCanTrade(true);
        } else {
            setCanTrade(false);
        }

        setLoading(false)

        return () => {
            setFromAssetAmount(fromAssetAmountInitial);
            setToAssetAmount(toAssetAmountInitial)

        }

    }, [balances, fromAsset, toAsset, toAssetsArray.length])

    useEffect(() => {
        getAssetsToTrade();
    }, [cryptoMode, toAsset]);

    const getAssetsToTrade = () => {

        const toAssets: Asset[] = [];

        supportedAssets
            .filter((asset: Asset) => {
                // Ensure the asset is either fiat or crypto based on the mode
                return (asset.type === 'fiat' || (cryptoMode && asset.type === 'crypto'))
                    // Make sure it's not the same as the fromAsset
                    && asset.identifier !== fromAsset.identifier
                    // Filter out assets where trade_enabled is false
                    && asset.trade_enabled === true
            })
            .forEach((asset: Asset) => {
                // Include all tradeable assets, regardless of whether they have a balance
                toAssets.push(asset);
            });

        setToAssetsArray(toAssets);

    }

    const handleSelectEvent = (assetCode: string) => {

        const selectedAsset = supportedAssets.find((asset: Asset) => {
            return asset.code === assetCode;
        });

        if (selectedAsset) {
            setToAsset(selectedAsset)
        }
    }

    useEffect(() => {
        if (toAssetsArray.length > 0 && !toAsset) {
            setToAsset(toAssetsArray[0]); // Set the first valid asset only if toAsset is not already set
        }
    }, [toAssetsArray, toAsset]); // Trigger only when toAssetsArray changes

    useEffect(() => {
        const checkNavigation = async () => {
            // Add delay for nav stack initialization
            await new Promise(resolve => setTimeout(resolve, 150));

            const canGoBackResult = await nav?.canGoBack();

            if (canGoBackResult) {
                setGoBack(true);
            }
        };

        checkNavigation();
    }, []);

    const handleNavigation = async () => {
        const canGoBackResult = await nav?.canGoBack();

        if (canGoBackResult) {
            nav?.pop();
        } else {
            close();
        }
    }


    return (
        <IonPage ref={pageRef}>

            <IonHeader className="ion-no-border">

                <IonToolbar style={{ marginTop: "0%" }}>
                    <IonButtons slot='start'>
                        <IonButton onClick={handleNavigation} color='primary'>
                            <IonIcon
                                slot="icon-only"
                                icon={goBack ? arrowBackOutline : closeOutline}
                            />
                        </IonButton>
                    </IonButtons>
                   <IonTitle>{t('trade.HEADER')} </IonTitle>

                </IonToolbar>

            </IonHeader>


            <IonContent>

                <div hidden={!canTrade}>

                    <div className="ion-text-center">
                        <IonNote color='primary' className='tradePage_IonNote'> {marketLabel}</IonNote>
                    </div>

                    <IonCard style={{ marginTop: '10%' }} >
                        <IonCardContent>
                            <IonGrid>
                                <IonRow>
                                    <IonCol size="6">
                                        <IonItem lines="none" >
                                            <IonThumbnail className="tradePageAssetImg" slot="start">
                                                <img src={fromAsset?.icon} />
                                            </IonThumbnail>
                                            <h4> {fromAsset?.display_code}</h4>
                                            {/* <IonIcon icon={chevronDownOutline} size='small' onClick={() => {setShowFromAssetPage(true)}}/> */}
                                        </IonItem>
                                        <IonNote className='tradePage_IonNote'>

                                            {
                                                t('trade.FROM-ASSET-BALANCE') + ': ' + (
                                                    fromAsset ?

                                                        balances[fromAsset.identifier] ?

                                                            decimalAdjust(balances[fromAsset.identifier].available, -fromAsset.decimal_places)
                                                            : 0

                                                        : 0)

                                            }

                                        </IonNote>
                                    </IonCol>
                                    <IonCol size="6">

                                        <IonInput
                                            mode="md"
                                            fill="outline"
                                            inputMode='decimal'
                                            clearOnEdit={true}
                                            type='text' value={fromAssetAmount}
                                            debounce={950} onIonInput={
                                                (e: any) => tradeAmount(e.detail.value!)}>

                                        </IonInput>




                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                        </IonCardContent>
                    </IonCard>
                    <div className='ion-text-center'>
                        <IonIcon className='tradePageIcon' icon={syncOutline} size='large' color='primary' onClick={() => { swapFromToAsset() }} />
                    </div>

                    <IonCard>
                        <IonCardContent>
                            <IonGrid>
                                <IonRow>
                                    <IonCol size="8">
                                        <IonItem lines="none">
                                            <IonThumbnail className="tradePageAssetImg" slot="start">
                                                <img src={toAsset?.icon} />
                                            </IonThumbnail>
                                            {
                                                toAssetsArray.length > 1
                                                    ? (
                                                        <IonSelect
                                                            interface="popover"
                                                            value={toAsset.code}
                                                            onIonChange={(e: SelectCustomEvent) => handleSelectEvent(e.detail.value)}
                                                        >
                                                            {
                                                                toAssetsArray.map((asset) => {
                                                                    return (
                                                                        <IonSelectOption
                                                                            key={asset.code}
                                                                            value={asset.code}
                                                                        >
                                                                            {asset.code}
                                                                        </IonSelectOption>
                                                                    )
                                                                })
                                                            }

                                                        </IonSelect>)
                                                    : <h4> {toAsset?.display_code}</h4>
                                            }

                                        </IonItem>
                                        <IonNote className='tradePage_IonNote'>
                                            {
                                                t('trade.FROM-ASSET-BALANCE') + ': ' + (
                                                    toAsset ?
                                                        balances[toAsset.identifier] ? decimalAdjust(balances[toAsset.identifier].available, -toAsset.decimal_places) : 0

                                                        : 0
                                                )

                                            }


                                        </IonNote>

                                    </IonCol>
                                    <IonCol size="4">
                                        <IonItem lines="none">

                                            <NumberFormat
                                                value={toAssetAmount}
                                                type='text'
                                                decimalSeparator="."
                                                decimalScale={toAsset?.decimal_places}
                                                fixedDecimalScale={true}
                                                allowNegative={false}
                                                isNumericString={true}
                                                customInput={IonInput}
                                                disabled={true}
                                                className="tradePageNumberFormat"
                                            />

                                        </IonItem>

                                    </IonCol>

                                </IonRow>
                            </IonGrid>
                        </IonCardContent>
                    </IonCard>

                </div>

                <div hidden={loading || canTrade} className="ion-text-center">

                    <p className="tradePageNoAssetsInfo">
                        {t('trade.NO-ASSETS-TO-TRADE')}
                    </p>

                </div>

                <div hidden={!loading}>
                    <IonSpinner className="spinner" name="crescent" color='primary'></IonSpinner>
                </div>

            </IonContent>
            <div id='pageFooter' hidden={!canTrade}>
                <IonToolbar>
                    <IonButton
                        type="submit"
                        color={"primary"}
                        onClick={() => { showTradeConfirmation() }}
                        disabled={disableButton}
                        expand="block"
                        id="pageFooterButton">
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            : t('trade.BTN.LABEL-1') + ' ' + fromAsset?.display_code + ' ' + t('trade.BTN.LABEL-2') + ' ' + (toAsset ? toAsset.display_code : '')
                        }

                    </IonButton>
                </IonToolbar>
            </div>
        </IonPage>
    )

}

const TradePage: React.FC<Props> = (props = {}) => (
    <Provider store={store}>
        <Page assetFrom={props.assetFrom} assetTo={props.assetTo} />
    </Provider>
);

export default TradePage;